/**
 * Cookie-free popup (templates/_components/_popup.twig).
 *
 * The server only outputs the newest enabled popup on article pages; everything per visitor
 * happens here, in the browser, so the pages stay cacheable.
 *
 *  - Trigger:  30 s of active time (tab visible) in this browser session.
 *  - Yes, No and close: the popup doesn't come back in this browser session (sessionStorage).
 *  - "Don't show again" checkbox, applied on Yes, No or close: the only choice kept past the
 *    session — that popup is suppressed for 30 days (localStorage). Agreed with the client: nothing about the visit itself may persist,
 *    since localStorage falls under the same consent rules as cookies (ePrivacy, art. 11.7a Tw).
 */
const TRIGGER_MS = 30 * 1000;
const SUPPRESS_MS = 30 * 24 * 60 * 60 * 1000;
const TICK_MS = 1000;
const MAX_TICK_GAP_MS = 2500; // a longer gap means the device slept, so don't count it
const EXIT_MS = 400; // length of the exit animation in _popup.scss

/**
 * Added to the Yes link unless the editor's URL already has a utm_ parameter.
 * utm_campaign is the popup's slug. Empty this object to stop adding UTM parameters.
 */
const UTM_DEFAULTS = {
  utm_source: 'popup',
  utm_medium: 'onsite',
};

/**
 * dataLayer events, each pushed with popup_id, popup_name (the popup's slug) and
 * popup_dont_show (whether "Don't show again" is ticked).
 *
 * Suggested GTM setup (nothing reaches GA4 until this is published):
 *  1. Variables → Data Layer Variable for popup_id and popup_name.
 *  2. Trigger → Custom Event, event name `^popup_` with "Use regex matching" on.
 *  3. Tag → Google Analytics: GA4 Event, event name {{Event}}, parameters popup_id and
 *     popup_name, fired by that trigger. Measurement ID: the site's existing GA4 tag.
 *  4. In GA4, register popup_id and popup_name as event-scoped custom dimensions, and mark
 *     popup_yes as a key event if the popups should count as conversions.
 */
const EVENTS = {
  view: 'popup_view',
  yes: 'popup_yes',
  no: 'popup_no',
  close: 'popup_close', // close button, backdrop or Esc
};

const read = (storage, key) => {
  try {
    return JSON.parse(window[storage].getItem(key));
  } catch (e) {
    return null;
  }
};

const write = (storage, key, value) => {
  try {
    window[storage].setItem(key, JSON.stringify(value));
    return true;
  } catch (e) {
    return false;
  }
};

const storageAvailable = (storage) => {
  try {
    const key = '__popup_test__';
    window[storage].setItem(key, '1');
    window[storage].removeItem(key);
    return true;
  } catch (e) {
    return false;
  }
};

// Earlier versions kept the visit and a 30-day flag after Yes in localStorage; clear those.
const removeLegacyKeys = () => {
  try {
    Object.keys(window.localStorage)
      .filter((key) => key.startsWith('popup:') && (key.endsWith(':visit') || key.endsWith(':converted')))
      .forEach((key) => window.localStorage.removeItem(key));
  } catch (e) {
    // localStorage blocked: nothing to clear
  }
};

const addUtm = (link, campaign) => {
  if (!link || !Object.keys(UTM_DEFAULTS).length) {
    return;
  }

  let url;
  try {
    url = new URL(link.href);
  } catch (e) {
    return;
  }
  if (!url.protocol.startsWith('http') || [...url.searchParams.keys()].some((key) => key.startsWith('utm_'))) {
    return;
  }

  Object.entries({...UTM_DEFAULTS, utm_campaign: campaign}).forEach(([key, value]) => {
    if (value) {
      url.searchParams.set(key, value);
    }
  });
  link.href = url.toString();
};

export function initPopup() {
  removeLegacyKeys();

  if (!storageAvailable('sessionStorage')) {
    return;
  }

  const dialog = document.querySelector('[data-popup]');
  const popupSite = window.globalInfo?.siteHandle || dialog?.dataset.popupSite || window.location.hostname;
  const visitKey = `popup:${popupSite}:visit`;

  const loadVisit = () => read('sessionStorage', visitKey) || {activeMs: 0, shown: false};

  let lastTick = Date.now();
  let timer = null;
  let done = false;

  const dismissedKey = () => `popup:${popupSite}:${dialog.dataset.popupId}:dismissed`;

  const isDismissed = () => {
    const dismissedAt = read('localStorage', dismissedKey());
    return Boolean(dismissedAt) && Date.now() - dismissedAt < SUPPRESS_MS;
  };

  const dontShowAgain = () => Boolean(dialog.querySelector('[data-popup-dismiss]')?.checked);

  const track = (event) => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event,
      popup_id: dialog.dataset.popupId,
      popup_name: dialog.dataset.popupSlug,
      popup_dont_show: dontShowAgain(),
    });
  };

  // Yes, No or close: track it and, only when the checkbox is ticked, keep it for 30 days.
  const answer = (event) => {
    track(event);
    if (dontShowAgain()) {
      write('localStorage', dismissedKey(), Date.now());
    }
  };

  const show = (visit) => {
    visit.shown = true;
    write('sessionStorage', visitKey, visit);
    done = true;
    stop();

    // Every layout is modal, so each one gets the dimmed backdrop (the slide-in only stays in its corner).
    dialog.showModal();
    // Focus the dialog itself (tabindex="-1"): keyboard users are inside it, and the close button
    // doesn't get a focus ring for mouse users.
    dialog.focus();
    track(EVENTS.view);
  };

  const tick = () => {
    if (done) {
      return;
    }
    const now = Date.now();
    const gap = now - lastTick;
    lastTick = now;

    // Re-read each time, in case the popup was shown in the meantime
    const visit = loadVisit();
    if (document.visibilityState === 'visible' && gap <= MAX_TICK_GAP_MS) {
      visit.activeMs += gap;
    }
    write('sessionStorage', visitKey, visit);

    if (dialog && !visit.shown && visit.activeMs >= TRIGGER_MS && !isDismissed()) {
      show(visit);
    }
  };

  function start() {
    if (timer === null && !done) {
      lastTick = Date.now();
      timer = window.setInterval(tick, TICK_MS);
    }
  }

  function stop() {
    if (timer !== null) {
      window.clearInterval(timer);
      timer = null;
    }
  }

  if (dialog) {
    addUtm(dialog.querySelector('[data-popup-yes]'), dialog.dataset.popupSlug);

    // Play the exit animation (.popup--closing) first, then really close the dialog.
    const close = (event) => {
      if (!dialog.open || dialog.classList.contains('popup--closing')) {
        return;
      }
      answer(event);
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        dialog.close();
        return;
      }
      dialog.classList.add('popup--closing');
      window.setTimeout(() => {
        dialog.close();
        dialog.classList.remove('popup--closing');
      }, EXIT_MS);
    };

    dialog.querySelector('[data-popup-close]')?.addEventListener('click', () => close(EVENTS.close));
    dialog.querySelector('[data-popup-no]')?.addEventListener('click', () => close(EVENTS.no));

    dialog.querySelector('[data-popup-yes]')?.addEventListener('click', () => answer(EVENTS.yes));

    // Click on the backdrop of a modal reports the dialog itself as the target.
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) {
        close(EVENTS.close);
      }
    });

    // ESC would close the dialog natively, without the exit animation.
    dialog.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        close(EVENTS.close);
      }
    });
    dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      close(EVENTS.close);
    });

    // Already shown in this session, or dismissed: nothing to do on this page.
    if (loadVisit().shown || isDismissed()) {
      done = true;
    }
  }

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      start();
    } else {
      stop();
    }
  });

  if (document.visibilityState === 'visible') {
    start();
  }
}
